import type { LucideIcon } from 'lucide-react';
import {
  Anchor,
  BarChart3,
  Beer,
  Bell,
  Bike,
  Bird,
  BookOpen,
  Box,
  Briefcase,
  Brush,
  Building,
  Building2,
  Bus,
  Cake,
  Calendar,
  Camera,
  Car,
  Cat,
  CircleDollarSign,
  Clock,
  Cloud,
  Code,
  Coffee,
  CreditCard,
  Crown,
  Database,
  Diamond,
  Dog,
  Dumbbell,
  Factory,
  Film,
  Fish,
  Flag,
  Flame,
  Flower2,
  Gamepad2,
  Gem,
  Gift,
  Globe,
  GraduationCap,
  Hammer,
  Handshake,
  Headphones,
  Headset,
  Heart,
  Home,
  Hospital,
  Hotel,
  IceCreamCone,
  Key,
  Landmark,
  Laptop,
  Layers,
  LayoutGrid,
  Leaf,
  Lightbulb,
  Lock,
  Mail,
  MapPin,
  Medal,
  Megaphone,
  MessageCircle,
  Mic,
  Monitor,
  Moon,
  Mountain,
  Music,
  Newspaper,
  Package,
  Palette,
  PawPrint,
  Phone,
  PieChart,
  Pill,
  Pizza,
  Plane,
  Presentation,
  Receipt,
  Rocket,
  School,
  Server,
  Settings,
  Shield,
  Ship,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Snowflake,
  Sparkles,
  Star,
  Stethoscope,
  Store,
  Sun,
  Tablet,
  Tag,
  Target,
  Train,
  Trees,
  Trophy,
  Truck,
  Tv,
  Umbrella,
  UserRound,
  Users,
  Utensils,
  Wallet,
  Warehouse,
  Wifi,
  Wine,
  Wrench,
  Zap,
} from 'lucide-react';

/**
 * The teamspace icon picker's icon set (Jessica, 2026-09-28): a curated list of lucide icons,
 * keyed by their kebab-case lucide name, which is what `team.icon` stores. Curated rather than all
 * of lucide so the picker stays scannable and the bundle only carries these.
 *
 * Order = picker order: places and businesses first (what teamspaces usually are), then themes.
 */
export const TEAMSPACE_ICONS: Record<string, LucideIcon> = {
  store: Store,
  'shopping-bag': ShoppingBag,
  'shopping-cart': ShoppingCart,
  building: Building,
  'building-2': Building2,
  landmark: Landmark,
  hotel: Hotel,
  hospital: Hospital,
  school: School,
  'graduation-cap': GraduationCap,
  factory: Factory,
  warehouse: Warehouse,
  truck: Truck,
  plane: Plane,
  car: Car,
  bus: Bus,
  train: Train,
  ship: Ship,
  'map-pin': MapPin,
  globe: Globe,
  home: Home,
  briefcase: Briefcase,
  users: Users,
  'user-round': UserRound,
  heart: Heart,
  star: Star,
  sparkles: Sparkles,
  flame: Flame,
  zap: Zap,
  sun: Sun,
  moon: Moon,
  cloud: Cloud,
  leaf: Leaf,
  trees: Trees,
  'flower-2': Flower2,
  coffee: Coffee,
  utensils: Utensils,
  pizza: Pizza,
  'ice-cream-cone': IceCreamCone,
  wine: Wine,
  beer: Beer,
  cake: Cake,
  dumbbell: Dumbbell,
  bike: Bike,
  trophy: Trophy,
  medal: Medal,
  target: Target,
  rocket: Rocket,
  lightbulb: Lightbulb,
  megaphone: Megaphone,
  bell: Bell,
  calendar: Calendar,
  clock: Clock,
  camera: Camera,
  film: Film,
  music: Music,
  headphones: Headphones,
  mic: Mic,
  tv: Tv,
  monitor: Monitor,
  smartphone: Smartphone,
  tablet: Tablet,
  laptop: Laptop,
  'gamepad-2': Gamepad2,
  palette: Palette,
  brush: Brush,
  'book-open': BookOpen,
  newspaper: Newspaper,
  stethoscope: Stethoscope,
  pill: Pill,
  shield: Shield,
  lock: Lock,
  key: Key,
  flag: Flag,
  gift: Gift,
  tag: Tag,
  package: Package,
  box: Box,
  layers: Layers,
  'layout-grid': LayoutGrid,
  'pie-chart': PieChart,
  'bar-chart-3': BarChart3,
  wrench: Wrench,
  hammer: Hammer,
  settings: Settings,
  anchor: Anchor,
  mountain: Mountain,
  umbrella: Umbrella,
  snowflake: Snowflake,
  dog: Dog,
  cat: Cat,
  fish: Fish,
  bird: Bird,
  'paw-print': PawPrint,
  crown: Crown,
  diamond: Diamond,
  gem: Gem,
  'circle-dollar-sign': CircleDollarSign,
  'credit-card': CreditCard,
  wallet: Wallet,
  receipt: Receipt,
  presentation: Presentation,
  handshake: Handshake,
  headset: Headset,
  phone: Phone,
  mail: Mail,
  'message-circle': MessageCircle,
  wifi: Wifi,
  server: Server,
  database: Database,
  code: Code,
};

/** Icon colour swatches (`team.iconColor`); the first is the default foreground. */
export const TEAMSPACE_ICON_COLORS = [
  '#313a46',
  '#1ab394',
  '#23c6c8',
  '#3b82f6',
  '#6366f1',
  '#a855f7',
  '#ec4899',
  '#ed5565',
  '#f97316',
  '#f8ac59',
  '#84cc16',
  '#6c757d',
] as const;
